import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  addTrustedSite,
  listTrustedSites,
  removeTrustedSite,
  type AddTrustedSiteParams,
  type RemoveTrustedSiteParams,
  type TrustedSite,
} from "@/lib/admin-api";

const TRUSTED_SITES_KEY = ["trusted-sites"] as const;

export interface UseTrustedSitesResult {
  sites: TrustedSite[];
  loading: boolean;
  failed: boolean;
  error: unknown;
}

export function useTrustedSites(): UseTrustedSitesResult {
  const { data, isPending, isError, error } = useQuery({
    queryKey: TRUSTED_SITES_KEY,
    queryFn: listTrustedSites,
  });
  return { sites: data?.sites ?? [], loading: isPending, failed: isError, error };
}

/** Adds a site, or fails with the 400/409 admin-service sends back verbatim. */
export function useAddTrustedSite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: AddTrustedSiteParams) => addTrustedSite(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TRUSTED_SITES_KEY });
    },
  });
}

/** Removes a site. Links to it go back to being marked sponsored. */
export function useRemoveTrustedSite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: RemoveTrustedSiteParams) => removeTrustedSite(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TRUSTED_SITES_KEY });
    },
  });
}
